import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  COMMISSIONING_WIZARD_REDUCER,
  commissioningWizardInitialState,
  commissioningWizardReducer,
} from "./commissioning_wizard_reducer";
import {
  addVerificationComments,
  fetchCommissioningProcedureData,
  fetchCommissioningProcedureDevice,
  fetchFinalVerificationStep,
  hardResetProcedure,
  startResetCommissioningProcedure,
  updateCommissiongProcedureAcceptUserAgreement,
  updateCommissiongProcedureDeviceStep,
  updateCommissiongProcedureFinalVerficationStep,
} from "../../../../../../scripts/api_v2/commissioning_client";
import { createContext, useEffect, useReducer, useState } from "react";
import useBackend from "../../../../../../hooks/use_backend";
import Snackbar from "../../../../../../scripts/snackbar";
import { REACT_QUERY_KEYS } from "../../../../../../enums/react_query_key_enum";
import { FINAL_VERIFICATION_ACTIONS } from "../enum/final_verification_actions";

export const CommissioningStateContext = createContext({});

export const CommissioningStateProvider = ({ children }) => {
  const backend = useBackend();
  const params = useParams();
  const queryClient = useQueryClient();
  const [commissioningState, dispatchCommissioningStateChanges] = useReducer(
    commissioningWizardReducer,
    commissioningWizardInitialState,
  );
  const [termsDialogOpen, setTermsDialogOpen] = useState(false);
  const [termsDialogData, setTermsDialogData] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);

  const {
    data: commissioningData,
    error: commissioningError,
    isLoading: isLoadingCommissioning,
  } = useQuery({
    queryKey: [REACT_QUERY_KEYS.COMMISSIONING_DATA, backend.fetch, params.site_id],
    queryFn: () => fetchCommissioningProcedureData(backend.fetch, params.site_id),
  });

  const {
    data: deviceData,
    error: deviceDataError,
    isLoading: isLoadingDeviceData,
  } = useQuery({
    queryKey: [
      REACT_QUERY_KEYS.COMMISSIONING_DEVICE_DATA,
      backend.fetch,
      params.site_id,
      commissioningState.selectedDeviceDbId,
      commissioningState?.selectedPhase?.phase_key,
    ],
    queryFn: () =>
      fetchCommissioningProcedureDevice(
        backend.fetch,
        params.site_id,
        commissioningState.selectedDeviceDbId,
        commissioningState?.selectedPhase?.phase_key,
      ),
    enabled: commissioningState.selectedDeviceId != null && commissioningState.selectedPhase != null,
  });

  const {
    data: finalVerificationPhaseStep,
    error: finalVerificationPhaseStepError,
    isLoading: isLoadingFinalVerificationPhaseSteps,
  } = useQuery({
    queryKey: [
      REACT_QUERY_KEYS.COMMISSIONING_FINAL_VERIFICATION_STEP,
      backend.fetch,
      params.site_id,
      commissioningState.selectedFinalVerificationStepId,
    ],
    queryFn: () =>
      fetchFinalVerificationStep(backend.fetch, params.site_id, commissioningState.selectedFinalVerificationStepId),
    enabled: commissioningState.selectedFinalVerificationStepId != null,
  });

  const {
    mutate: startResetMutation,
    isPending: isStartResetMutating,
    isSuccess: isStartResetSuccess,
    isError: isStartResetError,
  } = useMutation({
    mutationFn: async ({ action_type }) => {
      return await startResetCommissioningProcedure(backend.fetch, params.site_id, action_type);
    },
    onMutate: async ({ data, phaseAction }) => {
      await queryClient.cancelQueries({
        queryKey: [REACT_QUERY_KEYS.COMMISSIONING_DATA, backend.fetch, params.site_id],
      });
    },
    onError: async (err, variables, context) => {
      // Check if this is a terms not accepted error (403)
      if (err.status === 403) {
        let termsId = err.data?.terms_id;
        let termsVersion = err.data?.terms_version;
        let docType = err.data?.doc_type || "commissioning";

        // If terms data is not in separate fields, try parsing from message
        if (!termsId && err.data?.msg) {
          const idMatch = err.data.msg.match(/\(ID:\s*(\d+)\)/);
          const versionMatch = err.data.msg.match(/Required:\s*([\d.]+)/);

          if (idMatch) termsId = parseInt(idMatch[1]);
          if (versionMatch) termsVersion = versionMatch[1];
        }

        // If we have terms_id, show the dialog
        if (termsId) {
          // Store the action to retry after acceptance
          setPendingAction(variables);
          // Open terms dialog with the required terms
          setTermsDialogData({
            terms_id: termsId,
            terms_version: termsVersion,
            doc_type: docType,
          });
          setTermsDialogOpen(true);
          return;
        }
      }

      // Not a terms error, show regular error
      const errorMessage = err.message || err.toString();
      Snackbar.error("Could not update procedure state [" + errorMessage + "]");
    },
    // Always refetch after commit, error or success:
    onSettled: async (data, error, variables) => {
      dispatchCommissioningStateChanges({ type: COMMISSIONING_WIZARD_REDUCER.START_RESET, payload: null });
      await queryClient.invalidateQueries({
        queryKey: [REACT_QUERY_KEYS.COMMISSIONING_DATA, backend.fetch, params.site_id],
      });
      await queryClient.invalidateQueries({ queryKey: [REACT_QUERY_KEYS.COMMISSIONING_FINAL_VERIFICATION_STEP] });
      await queryClient.invalidateQueries({ queryKey: [REACT_QUERY_KEYS.COMMISSIONING_DEVICE_DATA] });
    },
  });

  const {
    mutate: hardResetMutation,
    isPending: isHardResetMutating,
    isSuccess: isHardResetSuccess,
    isError: isHardResetError,
  } = useMutation({
    mutationFn: async ({}) => {
      return await hardResetProcedure(backend.fetch, params.site_id);
    },
    onMutate: async ({ data, phaseAction }) => {
      await queryClient.cancelQueries({
        queryKey: [REACT_QUERY_KEYS.COMMISSIONING_DATA, backend.fetch, params.site_id],
      });
    },
    onError: async (err, variables, context) => {
      // Check if this is a terms not accepted error (403)
      if (err.status === 403) {
        let termsId = err.data?.terms_id;
        let termsVersion = err.data?.terms_version;
        let docType = err.data?.doc_type || "commissioning";

        // If terms data is not in separate fields, try parsing from message
        if (!termsId && err.data?.msg) {
          const idMatch = err.data.msg.match(/\(ID:\s*(\d+)\)/);
          const versionMatch = err.data.msg.match(/Required:\s*([\d.]+)/);

          if (idMatch) termsId = parseInt(idMatch[1]);
          if (versionMatch) termsVersion = versionMatch[1];
        }

        // If we have terms_id, show the dialog
        if (termsId) {
          // Store the action to retry after acceptance (use a marker for hard reset)
          setPendingAction({ _isHardReset: true });
          // Open terms dialog with the required terms
          setTermsDialogData({
            terms_id: termsId,
            terms_version: termsVersion,
            doc_type: docType,
          });
          setTermsDialogOpen(true);
          return;
        }
      }

      // Not a terms error, show regular error
      const errorMessage = err.message || err.toString();
      Snackbar.error("Could not update procedure state [" + errorMessage + "]");
    },
    // Always refetch after commit, error or success:
    onSettled: async (data, error, variables) => {
      dispatchCommissioningStateChanges({ type: COMMISSIONING_WIZARD_REDUCER.START_RESET, payload: null });
      await queryClient.invalidateQueries({ queryKey: [REACT_QUERY_KEYS.COMMISSIONING_DATA] });
      await queryClient.invalidateQueries({ queryKey: [REACT_QUERY_KEYS.COMMISSIONING_FINAL_VERIFICATION_STEP] });
      await queryClient.invalidateQueries({ queryKey: [REACT_QUERY_KEYS.COMMISSIONING_DEVICE_DATA] });
    },
  });

  const {
    mutate: deviceStepMutation,
    isPending: isDeviceStepMutating,
    isSuccess: isDeviceStepSuccess,
    isError: isDeviceStepError,
  } = useMutation({
    mutationFn: async ({ item_id, item_value, warnings }) => {
      return await updateCommissiongProcedureDeviceStep(backend.fetch, params.site_id, item_id, item_value, warnings);
    },
    onMutate: async ({ data, phaseAction }) => {
      await queryClient.cancelQueries({
        queryKey: [REACT_QUERY_KEYS.COMMISSIONING_DATA, backend.fetch, params.site_id],
      });
      await queryClient.cancelQueries({
        queryKey: [
          REACT_QUERY_KEYS.COMMISSIONING_DEVICE_DATA,
          backend.fetch,
          params.site_id,
          commissioningState.selectedDeviceDbId,
          commissioningState?.selectedPhase?.phase_key,
        ],
      });
    },
    onError: async (err, variables, context) => {
      // Check if this is a terms not accepted error (403)
      if (err.status === 403) {
        let termsId = err.data?.terms_id;
        let termsVersion = err.data?.terms_version;
        let docType = err.data?.doc_type || "commissioning";

        // If terms data is not in separate fields, try parsing from message
        if (!termsId && err.data?.msg) {
          const idMatch = err.data.msg.match(/\(ID:\s*(\d+)\)/);
          const versionMatch = err.data.msg.match(/Required:\s*([\d.]+)/);

          if (idMatch) termsId = parseInt(idMatch[1]);
          if (versionMatch) termsVersion = versionMatch[1];
        }

        // If we have terms_id, show the dialog
        if (termsId) {
          // Store the action to retry after acceptance
          setPendingAction(variables);
          // Open terms dialog with the required terms
          setTermsDialogData({
            terms_id: termsId,
            terms_version: termsVersion,
            doc_type: docType,
          });
          setTermsDialogOpen(true);
          return;
        }
      }

      // Not a terms error, show regular error
      const errorMessage = err.message || err.toString();
      Snackbar.error("Could not update procedure state [" + errorMessage + "]");
    },
    // Always refetch after commit, error or success:
    onSettled: async (data, error, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [REACT_QUERY_KEYS.COMMISSIONING_DATA, backend.fetch, params.site_id],
      });
      await queryClient.invalidateQueries({
        queryKey: [
          REACT_QUERY_KEYS.COMMISSIONING_DEVICE_DATA,
          backend.fetch,
          params.site_id,
          commissioningState.selectedDeviceDbId,
          commissioningState?.selectedPhase?.phase_key,
        ],
      });
    },
  });

  const {
    mutate: finalVerificationMutation,
    isPending: isFinalVerificationStepMutating,
    isSuccess: isFinalVerificationStepSuccess,
    isError: isFinalVerificationStepError,
  } = useMutation({
    mutationFn: async ({ item_id, item_value, action, comments }) => {
      if (action === FINAL_VERIFICATION_ACTIONS.ADD_COMMENTS) {
        return await addVerificationComments(backend.fetch, params.site_id, item_id, comments);
        //             item_id: step.step_id,
        //             item_value: true,
        //             action: FINAL_VERIFICATION_ACTIONS.ADD_COMMENTS,
        //             comments: dialogData['comments']
      } else if (action === FINAL_VERIFICATION_ACTIONS.ACCEPT_USER_AGREEMENT) {
        return await updateCommissiongProcedureAcceptUserAgreement(backend.fetch, params.site_id, item_id);
      } else {
        return await updateCommissiongProcedureFinalVerficationStep(
          backend.fetch,
          params.site_id,
          item_id,
          item_value,
          action,
          comments,
        );
      }
    },
    onMutate: async ({ data, phaseAction }) => {
      await queryClient.cancelQueries({
        queryKey: [REACT_QUERY_KEYS.COMMISSIONING_DATA, backend.fetch, params.site_id],
      });
      await queryClient.cancelQueries({
        queryKey: [
          REACT_QUERY_KEYS.COMMISSIONING_FINAL_VERIFICATION_STEP,
          backend.fetch,
          params.site_id,
          commissioningState.selectedFinalVerificationStepId,
        ],
      });
    },
    onError: async (err, variables, context) => {
      // Check if this is a terms not accepted error (403)
      if (err.status === 403) {
        let termsId = err.data?.terms_id;
        let termsVersion = err.data?.terms_version;
        let docType = err.data?.doc_type || "commissioning";

        // If terms data is not in separate fields, try parsing from message
        if (!termsId && err.data?.msg) {
          const idMatch = err.data.msg.match(/\(ID:\s*(\d+)\)/);
          const versionMatch = err.data.msg.match(/Required:\s*([\d.]+)/);

          if (idMatch) termsId = parseInt(idMatch[1]);
          if (versionMatch) termsVersion = versionMatch[1];
        }

        // If we have terms_id, show the dialog
        if (termsId) {
          // Store the action to retry after acceptance
          setPendingAction(variables);
          // Open terms dialog with the required terms
          setTermsDialogData({
            terms_id: termsId,
            terms_version: termsVersion,
            doc_type: docType,
          });
          setTermsDialogOpen(true);
          return;
        }
      }

      // Not a terms error, show regular error
      const errorMessage = err.message || err.toString();
      Snackbar.error("Could not update procedure state [" + errorMessage + "]");
    },
    // Always refetch after commit, error or success:
    onSettled: async (data, error, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [REACT_QUERY_KEYS.COMMISSIONING_DATA, backend.fetch, params.site_id],
      });
      await queryClient.invalidateQueries({
        queryKey: [
          REACT_QUERY_KEYS.COMMISSIONING_FINAL_VERIFICATION_STEP,
          backend.fetch,
          params.site_id,
          commissioningState.selectedFinalVerificationStepId,
        ],
      });
    },
  });

  // useEffects to dispatch new commissioning state to the reducer
  useEffect(() => {
    if (finalVerificationPhaseStep) {
      dispatchCommissioningStateChanges({
        type: COMMISSIONING_WIZARD_REDUCER.FINAL_VERIFICATION_STEP_RECEIVED,
        payload: finalVerificationPhaseStep,
      });
    }
  }, [finalVerificationPhaseStep]);

  // Handle T&C errors from final verification step query
  useEffect(() => {
    if (finalVerificationPhaseStepError && !termsDialogOpen) {
      const err = finalVerificationPhaseStepError;
      // Check if this is a terms not accepted error (403)
      if (err.status === 403) {
        let termsId = err.data?.terms_id;
        let termsVersion = err.data?.terms_version;
        let docType = err.data?.doc_type || "commissioning";

        // If terms data is not in separate fields, try parsing from message
        if (!termsId && err.data?.msg) {
          const idMatch = err.data.msg.match(/\(ID:\s*(\d+)\)/);
          const versionMatch = err.data.msg.match(/Required:\s*([\d.]+)/);

          if (idMatch) termsId = parseInt(idMatch[1]);
          if (versionMatch) termsVersion = versionMatch[1];
        }

        // If we have terms_id, show the dialog
        if (termsId) {
          // For queries, we don't need to store a pending action to retry
          // The query will automatically refetch when the dialog closes
          setTermsDialogData({
            terms_id: termsId,
            terms_version: termsVersion,
            doc_type: docType,
          });
          setTermsDialogOpen(true);
        }
      }
    }
  }, [finalVerificationPhaseStepError, termsDialogOpen]);

  // Handle T&C errors from device data query
  useEffect(() => {
    if (deviceDataError && !termsDialogOpen) {
      const err = deviceDataError;
      // Check if this is a terms not accepted error (403)
      if (err.status === 403) {
        let termsId = err.data?.terms_id;
        let termsVersion = err.data?.terms_version;
        let docType = err.data?.doc_type || "commissioning";

        // If terms data is not in separate fields, try parsing from message
        if (!termsId && err.data?.msg) {
          const idMatch = err.data.msg.match(/\(ID:\s*(\d+)\)/);
          const versionMatch = err.data.msg.match(/Required:\s*([\d.]+)/);

          if (idMatch) termsId = parseInt(idMatch[1]);
          if (versionMatch) termsVersion = versionMatch[1];
        }

        // If we have terms_id, show the dialog
        if (termsId) {
          // For queries, we don't need to store a pending action to retry
          // The query will automatically refetch when the dialog closes
          setTermsDialogData({
            terms_id: termsId,
            terms_version: termsVersion,
            doc_type: docType,
          });
          setTermsDialogOpen(true);
        }
      }
    }
  }, [deviceDataError, termsDialogOpen]);

  useEffect(() => {
    if (commissioningData) {
      dispatchCommissioningStateChanges({
        type: COMMISSIONING_WIZARD_REDUCER.COMMISSIONING_DATA_RECEIVED,
        payload: commissioningData,
      });
    }
  }, [commissioningData]);

  useEffect(() => {
    if (deviceData) {
      dispatchCommissioningStateChanges({
        type: COMMISSIONING_WIZARD_REDUCER.DEVICE_DATA_RECEIVED,
        payload: deviceData,
      });
    }
  }, [deviceData]); // Log only when the state changes

  const handleTermsAccept = () => {
    setTermsDialogOpen(false);
    setTermsDialogData(null);
    // Retry the original action
    if (pendingAction) {
      // Check if this was a hard reset action
      if (pendingAction._isHardReset) {
        hardResetMutation({});
      } else if (pendingAction.item_id && pendingAction.action !== undefined) {
        // Final verification action - has item_id and action properties
        finalVerificationMutation(pendingAction);
      } else if (pendingAction.item_id) {
        // Device step action - has item_id property only
        deviceStepMutation(pendingAction);
      } else {
        // Regular start/reset action
        startResetMutation(pendingAction);
      }
      setPendingAction(null);
    } else {
      // If no pending action (e.g., from a query error), reset and refetch the queries that might have errors
      // Use refetch instead of reset to preserve the query state and just retry the failed request
      queryClient.refetchQueries({
        queryKey: [REACT_QUERY_KEYS.COMMISSIONING_DEVICE_DATA],
        type: "active",
      });
      queryClient.refetchQueries({
        queryKey: [REACT_QUERY_KEYS.COMMISSIONING_FINAL_VERIFICATION_STEP],
        type: "active",
      });
    }
  };

  const handleTermsDecline = () => {
    setTermsDialogOpen(false);
    setTermsDialogData(null);
    setPendingAction(null);
  };

  const contextValue = {
    commissioningState,
    dispatchCommissioningStateChanges,
    commissioningData,
    commissioningError,
    isLoadingCommissioning,
    deviceData,
    deviceDataError,
    isLoadingDeviceData,
    finalVerificationPhaseStep,
    finalVerificationPhaseStepError,
    isLoadingFinalVerificationPhaseSteps,
    startResetMutation,
    isStartResetMutating,
    isStartResetSuccess,
    isStartResetError,
    deviceStepMutation,
    isDeviceStepMutating,
    isDeviceStepSuccess,
    isDeviceStepError,
    finalVerificationMutation,
    isFinalVerificationStepMutating,
    isFinalVerificationStepSuccess,
    isFinalVerificationStepError,
    hardResetMutation,
    isHardResetMutating,
    termsDialogOpen,
    termsDialogData,
    handleTermsAccept,
    handleTermsDecline,
  };

  return <CommissioningStateContext.Provider value={contextValue}>{children}</CommissioningStateContext.Provider>;
};
